import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import SurnameHeritageChart from './SurnameHeritageChart';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { 
  Users, 
  MapPin, 
  BookOpen, 
  Sparkles, 
  Globe, 
  Clock, 
  Flag,
  Heart,
  Search,
  Info,
  Download,
  FileText,
  UserPlus,
  Scroll
} from 'lucide-react';

const ComprehensiveTribalResults = ({ results }) => {
  const [isSavingToProfile, setIsSavingToProfile] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  if (!results) {
    return (
      <Card className="bg-white shadow-lg">
        <CardContent className="text-center py-12">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Results</h3>
          <p className="text-gray-600">Complete the tribal genealogy form to see your comprehensive analysis.</p>
        </CardContent>
      </Card>
    );
  }

  const { 
    determined_tribal_region, 
    comprehensive_tribal_analysis, 
    surname_analyses = [], 
    maternal_lineage_summary, 
    paternal_lineage_summary,
    cultural_connection_recommendations = [],
    further_research_recommendations = [],
    tribal_connection_confidence
  } = results;

  const handleDownloadReport = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/genealogy/download-tribal-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysis_results: results,
          generated_date: new Date().toISOString()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Tribal_Heritage_Analysis_${results.user_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download report. Please try again.');
    }
  };

  // Save Analysis to Profile
  const saveToProfile = async () => {
    // Allow admins to bypass subscription check
    const subscription_tier = user?.subscription_tier || 'free';
    const isAdmin = user?.role === 'admin';
    
    if (subscription_tier === 'free' && !isAdmin) {
      toast({
        variant: "destructive",
        title: "Upgrade Required",
        description: "Only Seekers Path and Crown subscribers can save analysis to their profile.",
      });
      return;
    }

    setIsSavingToProfile(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Please log in to save to profile');
      }

      const response = await fetch(`${BACKEND_URL}/api/genealogy/save-to-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          analysis_result: results,
          analysis_type: 'tribal'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to save: ${response.status}`);
      }

      const data = await response.json();
      
      toast({
        title: "✅ Saved to Profile!",
        description: `Your tribal analysis has been added to your public profile. ${data.tribes_saved || 0} tribes and ${data.surnames_saved || 0} surnames saved.`,
      });
      
    } catch (error) {
      console.error('Error saving to profile:', error);
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: error.message || "Unable to save to profile at this time. Please try again.",
      });
    } finally {
      setIsSavingToProfile(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 shadow-xl">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center text-2xl">
                <Sparkles className="w-8 h-8 mr-3 text-blue-600" />
                Comprehensive Tribal Heritage Analysis
              </CardTitle>
              <p className="text-gray-600 mt-2">
                Complete analysis for {results.user_name} • Analysis Date: {new Date(results.analysis_date).toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleDownloadReport}
                className="bg-gradient-to-r from-green-600 to-blue-600 text-white"
              >
                <Download className="mr-2 h-4 w-4" />
                Download Report
              </Button>
              <Button 
                onClick={saveToProfile}
                disabled={isSavingToProfile}
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                {isSavingToProfile ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Saving...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Save to Profile
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tribal Connection Card */}
      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Flag className="w-6 h-6 mr-2 text-green-600" />
            Tribal Connection Determined
            <Badge 
              variant={tribal_connection_confidence === 'High' ? 'default' : 'secondary'} 
              className="ml-3"
            >
              {tribal_connection_confidence}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {determined_tribal_region ? (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-xl font-bold text-green-800 mb-2">
                  {determined_tribal_region.name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-green-700">Historical Period:</span>
                    <p className="text-green-600">{determined_tribal_region.historical_period}</p>
                  </div>
                  <div>
                    <span className="font-medium text-green-700">Language Family:</span>
                    <p className="text-green-600">{determined_tribal_region.language_family}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <span className="font-medium text-green-700">Traditional Territory:</span>
                  <p className="text-green-600 text-sm mt-1">{determined_tribal_region.traditional_territory}</p>
                </div>
                <div className="mt-3">
                  <span className="font-medium text-green-700">Cultural Characteristics:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {determined_tribal_region.key_characteristics?.map((characteristic, index) => (
                      <Badge key={index} variant="outline" className="text-green-700 border-green-300">
                        {characteristic}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-medium text-yellow-800 mb-2">Tribal Connection Requires Further Research</h3>
              <p className="text-yellow-700 text-sm">
                Based on the provided genealogical information, a specific tribal connection could not be determined. 
                Additional location research or genealogical documentation may be needed.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Comprehensive Analysis Card */}
      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center">
            <BookOpen className="w-6 h-6 mr-2 text-purple-600" />
            AI-Powered Heritage Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="whitespace-pre-line text-gray-800 leading-relaxed">
              {comprehensive_tribal_analysis}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Surname Heritage Chart */}
      {surname_analyses.length > 0 && (
        <SurnameHeritageChart surname_analyses={surname_analyses} />
      )}

      {/* Germanic Origins Breakdown */}
      {surname_analyses.length > 0 && (() => {
        const germanicSurnames = surname_analyses.filter(analysis => 
          analysis.germanic_tribe && analysis.germanic_tribe.tribe_name
        );
        
        if (germanicSurnames.length === 0) return null;
        
        const germanicPercentage = ((germanicSurnames.length / surname_analyses.length) * 100).toFixed(1);
        
        // Count by tribe
        const tribeCount = {};
        germanicSurnames.forEach(analysis => {
          const tribe = analysis.germanic_tribe.tribe_name;
          tribeCount[tribe] = (tribeCount[tribe] || 0) + 1;
        });
        
        return (
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <Flag className="mr-2 h-6 w-6 text-purple-600" />
                Germanic Tribal Origins
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                {germanicPercentage}% of your family surnames have Germanic tribe connections
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(tribeCount)
                  .sort((a, b) => b[1] - a[1])
                  .map(([tribe, count], index) => {
                    const percentage = ((count / surname_analyses.length) * 100).toFixed(1);
                    return (
                      <div key={index} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-800 flex items-center gap-2">
                            <span className="text-2xl">⚔️</span>
                            {tribe}
                          </span>
                          <span className="text-lg font-bold text-purple-700">{percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-purple-500 to-pink-600 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-600">
                          {count} surname{count !== 1 ? 's' : ''} linked to {tribe} heritage
                        </p>
                      </div>
                    );
                  })}
              </div>
              
              {/* Additional Germanic Info */}
              <div className="mt-6 p-4 bg-purple-100 rounded-lg border border-purple-300">
                <p className="text-sm text-purple-900">
                  <strong>Germanic Heritage:</strong> Your family tree includes surnames with roots in ancient 
                  Germanic tribes that shaped European history. These tribes migrated across Europe between 
                  300-1000 CE, establishing kingdoms and influencing modern European cultures.
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Comprehensive Surname Analysis */}
      {surname_analyses.length > 0 && (
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Search className="w-6 h-6 mr-2 text-orange-600" />
              Comprehensive Surname Analysis
              <Badge variant="secondary" className="ml-3">
                {surname_analyses.length} Surnames Analyzed
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {surname_analyses.map((analysis, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Globe className="w-5 h-5 mr-2 text-blue-600" />
                    {analysis.surname}
                  </h4>
                  
                  {/* Early Origins of the Family Name - NEW SECTION */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-5 rounded-lg border-2 border-amber-200 mb-4">
                    <h5 className="font-bold text-amber-900 mb-3 flex items-center text-base">
                      <Scroll className="w-5 h-5 mr-2" />
                      📜 Early Origins of the Family Name
                    </h5>
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-800 leading-relaxed">
                        The surname <strong>{analysis.surname}</strong> originates from <strong>{analysis.primary_country_origin}</strong>. 
                        {analysis.geographic_distribution && (
                          <> This family name has been historically found in regions including {analysis.geographic_distribution}.</>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-800 mb-2">Country of Origin</h5>
                      <p className="text-gray-700 bg-blue-50 p-2 rounded text-sm">
                        {analysis.primary_country_origin}
                      </p>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-800 mb-2">Geographic Distribution</h5>
                      <p className="text-gray-700 bg-green-50 p-2 rounded text-sm">
                        {analysis.geographic_distribution}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h5 className="font-medium text-gray-800 mb-2">Etymology & Meaning</h5>
                    <p className="text-gray-700 bg-yellow-50 p-3 rounded text-sm leading-relaxed">
                      {analysis.etymology}
                    </p>
                  </div>

                  {analysis.spelling_variations && analysis.spelling_variations.length > 1 && (
                    <div className="mt-4">
                      <h5 className="font-medium text-gray-800 mb-2">Historical Spelling Variations</h5>
                      <div className="flex flex-wrap gap-2">
                        {analysis.spelling_variations.map((variation, idx) => (
                          <Badge key={idx} variant="outline" className="text-purple-700 border-purple-300">
                            {variation}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <h5 className="font-medium text-gray-800 mb-2">Historical Significance</h5>
                    <p className="text-gray-700 bg-gray-50 p-2 rounded text-sm">
                      {analysis.historical_significance}
                    </p>
                  </div>

                  <div className="mt-4">
                    <h5 className="font-medium text-gray-800 mb-2">Cultural Context</h5>
                    <p className="text-gray-700 bg-indigo-50 p-2 rounded text-sm">
                      {analysis.cultural_context}
                    </p>
                  </div>

                  {/* Germanic Tribe Information */}
                  {analysis.germanic_tribe && analysis.germanic_tribe.tribe_name && (
                    <div className="mt-4 bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
                      <h5 className="font-bold text-purple-900 mb-2 flex items-center">
                        <Flag className="w-5 h-5 mr-2" />
                        Germanic Tribal Origin: {analysis.germanic_tribe.tribe_name}
                      </h5>
                      {analysis.germanic_tribe.tribe_history && (
                        <div className="mb-3">
                          <h6 className="font-medium text-purple-800 text-sm mb-1">Tribe History:</h6>
                          <p className="text-sm text-purple-700 leading-relaxed">
                            {analysis.germanic_tribe.tribe_history}
                          </p>
                        </div>
                      )}
                      {analysis.germanic_tribe.surname_connection && (
                        <div>
                          <h6 className="font-medium text-purple-800 text-sm mb-1">Connection to Surname:</h6>
                          <p className="text-sm text-purple-700 leading-relaxed">
                            {analysis.germanic_tribe.surname_connection}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lineage Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Maternal Lineage */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Heart className="w-6 h-6 mr-2 text-pink-600" />
              Maternal Lineage Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="bg-pink-50 p-3 rounded">
                <h5 className="font-medium text-pink-800">Great Grandmother (Key Connection)</h5>
                <p className="text-sm text-pink-700 mt-1">
                  <strong>Name:</strong> {maternal_lineage_summary?.great_grandmother?.name}
                </p>
                <p className="text-sm text-pink-700">
                  <strong>Location:</strong> {maternal_lineage_summary?.great_grandmother?.location}
                </p>
                <p className="text-sm text-pink-700">
                  <strong>Tribal Connection:</strong> {maternal_lineage_summary?.great_grandmother?.tribal_connection}
                </p>
              </div>
              
              <div>
                <h5 className="font-medium text-gray-800">Grandmother</h5>
                <p className="text-sm text-gray-600">{maternal_lineage_summary?.grandmother?.name}</p>
                <p className="text-sm text-gray-500">{maternal_lineage_summary?.grandmother?.location}</p>
              </div>
              
              <div>
                <h5 className="font-medium text-gray-800">Mother</h5>
                <p className="text-sm text-gray-600">{maternal_lineage_summary?.mother?.name}</p>
                <p className="text-sm text-gray-500">{maternal_lineage_summary?.mother?.location}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paternal Lineage */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-6 h-6 mr-2 text-blue-600" />
              Paternal Lineage Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <h5 className="font-medium text-gray-800">Great Grandmother</h5>
                <p className="text-sm text-gray-600">{paternal_lineage_summary?.great_grandmother?.name}</p>
                <p className="text-sm text-gray-500">{paternal_lineage_summary?.great_grandmother?.location}</p>
              </div>
              
              <div>
                <h5 className="font-medium text-gray-800">Grandmother</h5>
                <p className="text-sm text-gray-600">{paternal_lineage_summary?.grandmother?.name}</p>
                <p className="text-sm text-gray-500">{paternal_lineage_summary?.grandmother?.location}</p>
              </div>
              
              <div>
                <h5 className="font-medium text-gray-800">Father</h5>
                <p className="text-sm text-gray-600">{paternal_lineage_summary?.father?.name}</p>
                <p className="text-sm text-gray-500">{paternal_lineage_summary?.father?.location}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Cultural Connection Recommendations */}
        {cultural_connection_recommendations.length > 0 && (
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center">
                <MapPin className="w-6 h-6 mr-2 text-green-600" />
                Cultural Connection Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {cultural_connection_recommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700">{recommendation}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Further Research Recommendations */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Info className="w-6 h-6 mr-2 text-blue-600" />
              Further Research Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {further_research_recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-sm text-gray-700">{recommendation}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Analysis Footer */}
      <Card className="bg-gray-50 shadow-lg">
        <CardContent className="pt-6 text-center">
          <p className="text-sm text-gray-600">
            This comprehensive analysis was generated using AI-powered genealogical research and should be used 
            as a starting point for further exploration of your Indigenous heritage. For official tribal enrollment 
            or legal purposes, consult with certified genealogists and tribal authorities.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComprehensiveTribalResults;